import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useGetLocationPartners, useGetLocations, useGetOrganizations } from 'hooks'
import { getFeatureVisibilityByPOS, getLoyaltyURL } from 'utils'
import SingleOrganizationDropdown from 'components/SingleOrganizationDropdown/SingleOrganizationDropdown'
import LoyaltyRewards from './LoyaltyRewards/LoyaltyRewards'
import LoyaltyDiners from './LoyaltyDiners/LoyaltyDiners'
import { CreateButton as UploadButton } from './LoyaltyRewards/styles'
import UploadCsvModal from './LoyaltyRewards/UploadCsvModal/UploadCsvModal'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { ReactComponent as SquareLogo } from 'assets/pos/square-logo.svg'
import { ChilipiperSection, LoadingSkeleton } from 'components'
import UnsupportedFeatureSplash from 'components/UnsupportedFeatureSplash/UnsupportedFeatureSplash'
import {
  BoldText,
  Container,
  DescriptionText,
  RuleBox,
  RuleTitle,
  RuleValue,
  RulesCardBox,
  RulesGrid,
  SectionTitle,
  TitleCardBox,
  TitleTypography,
  DropdownContainer,
  RulesGridButton,
  TemplateButton,
  ChilipiperSectionWrapper,
} from './styles'


const Loyalty: React.FC = () => {
  const [searchParams] = useSearchParams() 
  const { defaultLocation } = useGetLocations()
  const [isModalOpen, setModalOpen] = useState(false)
  const { defaultOrganization, setDefaultOrganizationAndUpdateURL } = useGetOrganizations()
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')
  const partnerId = defaultLocation?.id ? String(defaultLocation.id) : null
  const { partner } = useGetLocationPartners(partnerId ?? undefined)
  const { showLoyalty } = partner ? getFeatureVisibilityByPOS(partner) : { showLoyalty: false }
  
  // Select organization logic (extract and centralize it)
  const selectOrganization = useCallback(
    (id: string) => {
      setSelectedOrganizationId(id)
      setDefaultOrganizationAndUpdateURL(id, (orgId) => getLoyaltyURL(orgId))
    },
    [setDefaultOrganizationAndUpdateURL]
  )

  // Sync organization from URL or fallback
  useEffect(() => {
    const orgIdFromURL = searchParams.get('orgId')
    const orgIdByDefaultLocation = defaultLocation?.organization_id

    // Check if there is an orgId in the URL
    if (orgIdFromURL) {
      selectOrganization(orgIdFromURL)
    } 
    // If there is no orgId, we give priority to the default location's organization
    else if (orgIdByDefaultLocation) {
      selectOrganization(String(orgIdByDefaultLocation))
    }
    // If no orgId in the URL, but there is a defaultOrganization, use it
    else if (defaultOrganization?.id) {
      selectOrganization(String(defaultOrganization.id))
    }  
  }, [searchParams, defaultLocation?.organization_id, defaultOrganization?.id, selectOrganization])

  // Handle organization change from the dropdown
  const handleOrganizationChange = (id: number) => {
    selectOrganization(String(id))
  }

  if (!partner) return <LoadingSkeleton lineCount={3} />
  
  return (
    <Container>
      <TitleCardBox>
        <TitleTypography>
          {defaultOrganization?.name ? `Loyalty Program - ${defaultOrganization?.name}` : 'Loyalty Program'}
        </TitleTypography>
        <DescriptionText>
          <BoldText>Chowly Loyalty</BoldText> allows you to easily manage and set up loyalty rewards
          for your diners.
        </DescriptionText>
      </TitleCardBox>

      <DropdownContainer>
        <SingleOrganizationDropdown onOrganizationChange={handleOrganizationChange} />
      </DropdownContainer>
      {showLoyalty ? (
        <>
        <RulesCardBox>
          <SectionTitle>Loyalty Rules</SectionTitle>
          <RulesGrid>
            <RuleBox>
              <RuleTitle>Earning Type Rule</RuleTitle>
              <RuleValue>Dollar-per-point</RuleValue>
            </RuleBox>
            <RuleBox>
              <RuleTitle>Point Value</RuleTitle>
              <RuleValue>1 point for every $1 spent</RuleValue>
            </RuleBox>
          </RulesGrid>
          <RulesGridButton>
            <TemplateButton onClick={() => window.open('/example-template.csv', '_blank')}>
              <FileDownloadIcon />
              Template
            </TemplateButton>
            <UploadButton onClick={() => setModalOpen(true)}>
              Upload CSV
            </UploadButton>
          </RulesGridButton>
        </RulesCardBox>
        <LoyaltyRewards organizationId={selectedOrganizationId} />
        <LoyaltyDiners organizationId={selectedOrganizationId} />
        <UploadCsvModal
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          organizationId={selectedOrganizationId}
        />
      </>
    ) : (
      <>
        <ChilipiperSectionWrapper>
          <ChilipiperSection
            message='Loyalty program are available to platform users. Upgrade now to unlock this feature.'
            actionBtnText='Reach out to sales to learn how you can upgrade to a Platform Plan'
          />
        </ChilipiperSectionWrapper>
        <UnsupportedFeatureSplash
          title={`Loyalty setup happens in your Square dashboard`}
          description={`Since you're using Square as your POS system, loyalty program configuration needs to be managed directly through your Square account. Once configured there, your settings will automatically sync with Chowly.`}
          icon={<SquareLogo style={{ width: 64, height: 64 }} />}
          onClick={() => window.open('https://squareup.com/dashboard', '_blank')}
          buttonText={`Open Square Dashboard`}
          features={[
            {
              title: `1. Configure in Square`,
              description: `Set up your loyalty program settings directly in your Square dashboard.`,
            },
            {
              title: '2. Automatic Sync',
              description: 'Your loyalty program data will automatically appear in Chowly within a few minutes.',
            },
            {
              title: '3. View Status',
              description: 'Once synced, you\'ll be able to view and monitor your loyalty program performance right here in Chowly.',
            },
          ]}
        />
      </>
    )}
    </Container>
  )
}

export default Loyalty
